import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Building2,
  Calendar,
  Clock,
  FileText,
  Mic,
  MicOff,
  Phone,
  PhoneCall,
  PhoneOff,
  Sparkles,
  User,
} from "lucide-react";
import { Modal } from "../../components/ui/modal";
import { DateTimePicker } from "../../components/ui/date-picker";
import { core } from "../../lib/api";
import { extractList } from "../home/queries";
import { preflightMicrophone, useCallAudio } from "../supervision/audio/use-call-audio";
import type { CallAudioGrant } from "../supervision/types";
import { contactIdOf, CALL_OUTCOME_STATUS, type CallOutcome, type ComplianceMatch, type Lead } from "./types";
import { crmApi } from "./api";
import { ComplianceBlockedModal } from "./compliance-blocked-modal";
import { cn } from "../../lib/utils";

type Did = { id?: string; did_id?: string; number: string };
type CallState = {
  call_id: string;
  status: string;
  manual_ready: boolean;
  operator_connected: boolean;
  transcript_json: { speaker: string; text: string }[];
  duration_s?: number;
};
type Outcome = CallOutcome;
type LogData = { status: string; notes?: string; followUp?: string };
const terminal = (status: string) => ["completed", "failed", "cancelled", "not_picked_up"].includes(status);
const statusFor: Record<Outcome, string> = CALL_OUTCOME_STATUS;
const time = (seconds: number) =>
  `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

const getInitials = (name?: string) => {
  if (!name || !name.trim()) return "?";
  const parts = name.trim().split(/\s+/);
  return parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase();
};

export function ManualCallModal({
  lead,
  onClose,
  onLog,
}: {
  lead: Lead;
  onClose: () => void;
  onLog: (data: LogData) => void;
}) {
  const audio = useCallAudio();
  const cache = useQueryClient();
  const [logId] = useState(() => crypto.randomUUID());
  const [nextAction, setNextAction] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [call, setCall] = useState<CallState | null>(null);

  // Pre-dial DND check: suppressed contacts never reach the dialer.
  const [checkingCompliance, setCheckingCompliance] = useState(!!lead.phone);
  const [blockedMatches, setBlockedMatches] = useState<ComplianceMatch[] | null>(null);
  const [phase, setPhase] = useState<"connecting" | "calling" | "log">("connecting");
  const [elapsed, setElapsed] = useState(0);
  const [outcome, setOutcome] = useState<Outcome>("Connected");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("Contacted");
  const [followUp, setFollowUp] = useState(false);
  const [followUpAt, setFollowUpAt] = useState("");
  const [followUpNote, setFollowUpNote] = useState("");

  const activeId = useRef<string | null>(null);
  const sessionId = useRef<string | null>(null);
  const startedAt = useRef<number | null>(null);
  const autoStarted = useRef(false);
  const mounted = useRef(true);

  const { data: numbers = [], isLoading } = useQuery({
    queryKey: ["voice", "did-options"],
    queryFn: () => core<unknown>("/api/v1/voice/did-options").then(extractList) as Promise<Did[]>,
  });

  const path = (id: string) => `/api/v1/calls/manual/${encodeURIComponent(id)}`;

  const refresh = async (id: string) => {
    const next = await core<CallState>(path(id));
    if (sessionId.current !== id) return next;
    if (mounted.current) setCall(next);
    // The agent worker only flips status to "in_progress" once it actually
    // observes the customer's side answering (SIP callStatus / their audio
    // track). Timing the elapsed counter off this keeps it from ticking
    // (and the UI from claiming "Connected") through ring time.
    if (next.status === "in_progress" && startedAt.current === null) startedAt.current = Date.now();
    if (terminal(next.status)) {
      activeId.current = null;
      await audio.teardown();
      if (mounted.current) setPhase("log");
    }
    return next;
  };

  const start = async (did: Did) => {
    if (busy || activeId.current) return;
    setBusy(true);
    setError("");
    try {
      const didId = did.did_id || did.id;
      if (!didId) throw new Error("Your calling number is not configured correctly.");
      await preflightMicrophone();
      const prepared = await core<{ call_id: string; grant: CallAudioGrant }>("/api/v1/calls/manual", {
        method: "POST",
        body: JSON.stringify({
          contact_id: contactIdOf(lead),
          name: lead.name,
          phone: lead.phone,
          did_number_id: didId,
          metadata: {
            ...lead.values,
            crm_campaign_id: lead.campaignId,
            notes: lead.notes || "",
          },
        }),
      });
      activeId.current = prepared.call_id;
      sessionId.current = prepared.call_id;
      await audio.connect(prepared.grant, prepared.call_id);
      for (let attempt = 0; attempt < 30 && mounted.current; attempt++) {
        const state = await refresh(prepared.call_id);
        if (terminal(state.status)) throw new Error("The call could not be prepared. Please try again.");
        if (state.manual_ready && state.operator_connected) {
          await core(`${path(prepared.call_id)}/dial`, { method: "POST" });
          setPhase("calling");
          await refresh(prepared.call_id);
          return;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 1000));
      }
      throw new Error("Recording and microphone preparation timed out.");
    } catch (e) {
      const id = activeId.current;
      if (id) {
        try {
          await core(`${path(id)}/end`, { method: "POST" });
        } catch {
          /* cleanup happens when closing */
        }
      }
      activeId.current = null;
      await audio.teardown();
      if (mounted.current) setError(e instanceof Error ? e.message : "Unable to start call.");
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  // Pre-dial DND gate runs first; the auto-start below waits for it.
  useEffect(() => {
    if (!lead.phone) {
      setCheckingCompliance(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await crmApi.complianceCheck(lead.phone);
        if (cancelled) return;
        if (result.suppressed) {
          setBlockedMatches(result.matches);
          try {
            await crmApi.log(lead.id, {
              id: crypto.randomUUID(),
              kind: "compliance",
              notes: `Dial blocked — ${result.matches.map((m) => m.reason).join(", ") || "suppression list"}`,
            });
            await cache.invalidateQueries({ queryKey: ["crm-history", lead.id] });
          } catch {
            /* audit is best-effort */
          }
        }
      } catch {
        // fail open to the normal flow
      } finally {
        if (!cancelled) setCheckingCompliance(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (autoStarted.current || isLoading || checkingCompliance || blockedMatches) return;
    autoStarted.current = true;
    if (!numbers.length) {
      setError("No calling number is assigned to your account. Ask an administrator to add an allowed DID.");
      return;
    }
    void start(numbers[0]);
  }, [isLoading, numbers, checkingCompliance, blockedMatches]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (activeId.current) void core(`${path(activeId.current)}/end`, { method: "POST", keepalive: true }).catch(() => undefined);
    };
  }, []);

  useEffect(() => {
    if (phase !== "calling") return;
    const timer = window.setInterval(
      () => setElapsed(Math.floor((Date.now() - (startedAt.current || Date.now())) / 1000)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [phase]);

  useEffect(() => {
    if (!call || phase === "log") return;
    const timer = window.setInterval(
      () => void refresh(call.call_id).catch((e) => mounted.current && setError(e.message)),
      2000,
    );
    return () => window.clearInterval(timer);
  }, [call?.call_id, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const end = async () => {
    const id = activeId.current;
    if (!id) {
      setPhase("log");
      return;
    }
    setBusy(true);
    try {
      await core(`${path(id)}/end`, { method: "POST" });
      activeId.current = null;
      await audio.teardown();
      await refresh(id);
      setPhase("log");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to end the call. Please retry.");
    } finally {
      setBusy(false);
    }
  };

  const saveLog = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      if (followUp && (!followUpAt || !followUpNote.trim() || new Date(followUpAt).getTime() <= Date.now())) {
        throw new Error("Enter a future follow-up time and what to discuss.");
      }
      await crmApi.log(lead.id, {
        id: logId,
        kind: "call",
        callId: sessionId.current || undefined,
        outcome,
        notes,
        nextAction,
        status,
        followUp: followUp ? { dueAt: new Date(followUpAt).toISOString(), note: followUpNote } : undefined,
      });
      onLog({ status });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const safeClose = () => {
    if (!busy && !activeId.current) onClose();
  };

  if (blockedMatches) {
    return <ComplianceBlockedModal leadName={lead.name || lead.phone || "This lead"} matches={blockedMatches} onClose={onClose} />;
  }

  if (checkingCompliance) {
    return (
      <Modal title="Preparing Call" onClose={safeClose} width="max-w-md">
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <div className="relative mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand/10 text-brand">
            <span className="absolute h-full w-full animate-ping rounded-full bg-brand/20" />
            <PhoneCall className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-ink">Checking calling compliance…</p>
          <p className="mt-1 text-xs text-ink-3">Verifying suppression list and DND status</p>
        </div>
      </Modal>
    );
  }

  if (phase === "log") {
    return (
      <Modal
        title="Log Call Outcome"
        subtitle={
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink-2">
            <span className="font-semibold text-ink">{lead.name || "Unnamed lead"}</span>
            {lead.company && <span>· {lead.company}</span>}
            <span className="inline-flex items-center gap-1 rounded-full border border-brand/25 bg-brand/10 px-2 py-0.5 font-mono text-[11px] font-semibold text-brand">
              <Clock className="h-3 w-3" /> {time(call?.duration_s ?? elapsed)}
            </span>
          </div>
        }
        onClose={safeClose}
        width="max-w-xl"
        footer={
          <>
            <button
              disabled={busy}
              onClick={onClose}
              className="rounded-xl bg-soft px-4 py-2 text-sm font-semibold text-ink hover:bg-line transition-colors disabled:opacity-50"
            >
              Discard
            </button>
            <button
              disabled={busy}
              onClick={saveLog}
              className="rounded-xl bg-brand hover:bg-brand-hover px-5 py-2 text-sm font-semibold text-white shadow-xs transition-colors disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save Call Log"}
            </button>
          </>
        }
      >
        <div>
          <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-ink-3">Call Outcome *</label>
          <div className="grid grid-cols-3 gap-2">
            {(Object.keys(statusFor) as Outcome[]).map((item) => (
              <button
                key={item}
                type="button"
                disabled={busy}
                onClick={() => {
                  setOutcome(item);
                  setStatus(statusFor[item]);
                }}
                className={cn(
                  "rounded-xl border px-3 py-2 text-xs font-semibold transition-all disabled:opacity-50",
                  outcome === item
                    ? "border-brand bg-brand/15 text-brand shadow-xs ring-1 ring-brand/30"
                    : "border-line text-ink-2 hover:bg-soft hover:text-ink",
                )}
              >
                {item}
              </button>
            ))}
          </div>

          <label className="mt-4 block text-xs font-bold uppercase tracking-wider text-ink-3">
            Call Notes
            <textarea
              value={notes}
              disabled={busy}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What did the customer say? Key points, objections, next steps..."
              className="mt-1.5 min-h-24 w-full rounded-xl border border-line bg-canvas p-3 text-sm text-ink placeholder:text-ink-3 outline-none focus:border-brand focus:ring-1 focus:ring-brand disabled:opacity-50"
            />
          </label>

          <label className="mt-4 block text-xs font-bold uppercase tracking-wider text-ink-3">
            Update Customer Status
            <select
              value={status}
              disabled={busy}
              onChange={(e) => setStatus(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-line bg-canvas px-3 py-2.5 text-sm text-ink outline-none focus:border-brand disabled:opacity-50"
            >
              <option>Contacted</option>
              <option>Interested</option>
              <option>Follow-up</option>
              <option>Not interested</option>
              <option>Fresh lead</option>
            </select>
          </label>

          <div className="mt-4 rounded-xl border border-line bg-soft/70 p-3.5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-ink flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-brand" /> Schedule a Follow-up
                </p>
                <p className="text-[11px] text-ink-3">Add a reminder so this prospect doesn't slip through</p>
              </div>
              <button
                type="button"
                aria-pressed={followUp}
                disabled={busy}
                onClick={() => setFollowUp((v) => !v)}
                className={cn(
                  "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50",
                  followUp ? "bg-brand" : "bg-line",
                )}
              >
                <span
                  className={cn(
                    "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out",
                    followUp ? "translate-x-5" : "translate-x-0",
                  )}
                />
              </button>
            </div>
            {followUp && (
              <div className="mt-3 space-y-2.5 border-t border-line/60 pt-3">
                <div>
                  <span className="block text-xs font-semibold text-ink-2 mb-1">Due Date & Time</span>
                  <DateTimePicker
                    value={followUpAt}
                    disabled={busy}
                    onChange={(val) => setFollowUpAt(val)}
                    placeholder="Pick date & time"
                    className="w-full"
                  />
                </div>
                <label className="block text-xs font-semibold text-ink-2">
                  What to Discuss
                  <input
                    value={followUpNote}
                    disabled={busy}
                    onChange={(e) => setFollowUpNote(e.target.value)}
                    placeholder="e.g. Send the revised quote and product deck"
                    className="mt-1 w-full rounded-xl border border-line bg-canvas px-3 py-2 text-sm text-ink placeholder:text-ink-3 outline-none focus:border-brand"
                  />
                </label>
              </div>
            )}
          </div>

          <label className="mt-4 block text-xs font-bold uppercase tracking-wider text-ink-3">
            Next Action
            <input
              value={nextAction}
              disabled={busy}
              maxLength={2000}
              onChange={(e) => setNextAction(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-line bg-canvas p-2.5 text-sm text-ink placeholder:text-ink-3 outline-none focus:border-brand"
              placeholder="e.g. Send pricing proposal via WhatsApp"
            />
          </label>

          {error && (
            <div className="mt-3 flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 p-2.5 text-xs text-red-500">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>
      </Modal>
    );
  }

  // "dialing" is ringing — only "in_progress" means customer answered.
  const connected = call?.status === "in_progress";
  const initials = getInitials(lead.name);

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand/10 text-brand">
            <PhoneCall className="h-4 w-4" />
          </span>
          <div>
            <span className="text-sm font-bold text-ink">Direct Desk Call</span>
            <span className="ml-2 font-mono text-[11px] text-ink-3">WebRTC Live</span>
          </div>
        </div>
      }
      onClose={safeClose}
      width="max-w-md"
      hideCloseButton={true}
      preventDismiss={true}
      bodyClassName="p-3.5 sm:p-4"
      footer={
        <button
          disabled={busy || !!error}
          onClick={end}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 hover:bg-red-700 active:scale-[0.99] px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all disabled:opacity-50 cursor-pointer"
        >
          <PhoneOff className="h-4 w-4" /> End Call & Log Outcome
        </button>
      }
    >
      {/* Hero Call Presentation Card */}
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-stone-900 via-stone-900/95 to-stone-950 p-4 sm:p-5 text-center text-white shadow-xl">
        {/* Subtle dynamic ambient glow */}
        <div
          className={cn(
            "pointer-events-none absolute -top-16 left-1/2 -translate-x-1/2 h-36 w-72 rounded-full blur-3xl transition-all duration-700",
            connected ? "bg-emerald-500/20" : "bg-brand/20",
          )}
        />

        {/* Animated Avatar */}
        <div className="relative mx-auto flex h-16 w-16 items-center justify-center">
          {/* Outer ripple rings */}
          <div
            className={cn(
              "absolute -inset-2 rounded-full transition-all",
              connected ? "bg-emerald-500/20 animate-pulse" : "bg-brand/20 animate-ping opacity-60",
            )}
          />
          <div
            className={cn(
              "absolute -inset-1 rounded-full border transition-all",
              connected ? "border-emerald-500/40" : "border-brand/40",
            )}
          />
          {/* Avatar core */}
          <div
            className={cn(
              "relative flex h-16 w-16 items-center justify-center rounded-full text-xl font-bold text-white shadow-lg transition-all",
              connected
                ? "bg-gradient-to-br from-emerald-600 to-teal-700 shadow-emerald-950/50"
                : "bg-gradient-to-br from-brand to-orange-600 shadow-orange-950/50",
            )}
          >
            {initials}
          </div>
        </div>

        {/* Contact Info */}
        <h3 className="mt-3 text-lg font-bold tracking-tight text-white">{lead.name || "Unnamed contact"}</h3>
        {lead.company && (
          <p className="mt-0.5 flex items-center justify-center gap-1.5 text-xs font-medium text-stone-300">
            <Building2 className="h-3 w-3 text-stone-400" />
            {lead.company}
          </p>
        )}
        <p className="mt-0.5 flex items-center justify-center gap-1.5 font-mono text-xs font-medium text-stone-400">
          <Phone className="h-3 w-3 text-stone-500" />
          {lead.phone || "No phone number"}
        </p>

        {/* Status Pill Badge */}
        <div className="mt-2.5 flex justify-center">
          <div
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3 py-0.5 text-xs font-semibold transition-all",
              connected
                ? "border border-emerald-500/30 bg-emerald-500/15 text-emerald-400"
                : busy
                  ? "border border-blue-500/30 bg-blue-500/15 text-blue-400"
                  : "border border-amber-500/30 bg-amber-500/15 text-amber-400",
            )}
          >
            <span className="relative flex h-2 w-2">
              <span
                className={cn(
                  "absolute inline-flex h-full w-full rounded-full opacity-75",
                  connected ? "animate-ping bg-emerald-400" : "animate-ping bg-amber-400",
                )}
              />
              <span
                className={cn(
                  "relative inline-flex h-2 w-2 rounded-full",
                  connected ? "bg-emerald-500" : busy ? "bg-blue-500" : "bg-amber-500",
                )}
              />
            </span>
            <span>{connected ? "Connected · In Call" : busy ? "Connecting audio…" : "Calling / Ringing…"}</span>
          </div>
        </div>

        {/* Call Timer */}
        <div className="mt-2">
          <span className="font-mono text-2xl sm:text-3xl font-bold tracking-wider text-white">{time(elapsed)}</span>
        </div>

        {/* Audio Wave Visualizer Bars */}
        <div className="mt-2 flex h-5 items-center justify-center gap-1">
          {[35, 65, 30, 85, 55, 95, 45, 75, 40, 90, 60, 30].map((h, i) => (
            <span
              key={i}
              className={cn(
                "w-1 rounded-full transition-all duration-300",
                connected && audio.micLive
                  ? "bg-emerald-400 animate-audio-bar"
                  : connected
                    ? "bg-emerald-500/40"
                    : "bg-amber-400/30 animate-pulse",
              )}
              style={{
                height: connected && audio.micLive ? undefined : `${h * 0.16}px`,
                animationDelay: `${(i % 5) * 0.14}s`,
              }}
            />
          ))}
        </div>

        {/* In-Call Microphone Toggle */}
        <div className="mt-2.5 flex justify-center">
          <button
            type="button"
            disabled={busy}
            onClick={() => void audio.setMicEnabled(!audio.micLive)}
            className={cn(
              "inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold shadow-xs transition-all active:scale-95 disabled:opacity-50 cursor-pointer",
              audio.micLive
                ? "border border-white/20 bg-white/10 text-white hover:bg-white/15"
                : "border border-red-500/40 bg-red-500/20 text-red-300 hover:bg-red-500/30",
            )}
          >
            {audio.micLive ? (
              <>
                <Mic className="h-3.5 w-3.5 text-emerald-400" />
                <span>Mute Microphone</span>
              </>
            ) : (
              <>
                <MicOff className="h-3.5 w-3.5 text-red-400" />
                <span>Unmute Microphone</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Customer Context Section */}
      <div className="mt-3 rounded-xl border border-line bg-soft/70 p-3">
        <div className="flex items-center justify-between border-b border-line/60 pb-1.5">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-ink-3">
            <Sparkles className="h-3.5 w-3.5 text-brand" /> Context for this call
          </div>
          <span className="font-mono text-[10px] text-ink-3">{contactIdOf(lead)}</span>
        </div>

        <dl className="mt-2 grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-lg border border-line/60 bg-canvas/60 p-2">
            <dt className="text-[11px] text-ink-3">Status</dt>
            <dd className="mt-0.5 font-semibold text-ink">{lead.status || "Fresh lead"}</dd>
          </div>
          <div className="rounded-lg border border-line/60 bg-canvas/60 p-2">
            <dt className="text-[11px] text-ink-3">Source</dt>
            <dd className="mt-0.5 font-semibold text-ink">{lead.source || "CRM"}</dd>
          </div>
        </dl>

        {lead.notes && (
          <div className="mt-2 rounded-lg border border-line/60 bg-canvas/60 p-2 text-xs">
            <div className="mb-0.5 flex items-center gap-1 text-[11px] font-semibold text-ink">
              <FileText className="h-3 w-3 text-brand" /> Saved notes
            </div>
            <p className="line-clamp-2 leading-relaxed whitespace-pre-wrap text-ink-2">{lead.notes}</p>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-2.5 flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 p-2 text-xs text-red-500" role="alert">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </Modal>
  );
}
